// src/utils/attribution.js
//
// First-party lead attribution. On page load (src/index.js, browser only) the
// campaign parameters of the landing URL (utm_*, Google Ads click ids), the
// landing page and the external referrer are kept in sessionStorage for the
// rest of the visit. Nothing is sent anywhere from here: the quote forms read
// it when the visitor submits (with the consent box ticked), and the
// first-party page_view events (usePageView, only after cookie consent) read it.
//
// Never read this during render: the prerendered HTML has no session, so the
// first client render must not depend on it (hydration). Read it at submit.

const KEY = "lead_attribution";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"];
export const CAMPAIGN_KEYS = [...UTM_KEYS, ...CLICK_ID_KEYS];
// Every field the forms send (all strings; empty ones are sent as null)
export const ATTRIBUTION_FIELDS = [...CAMPAIGN_KEYS, "landing_page", "referrer"];

const MAX_LEN = { landing_page: 500, referrer: 500 };
const maxLen = (key) => MAX_LEN[key] || (UTM_KEYS.includes(key) ? 200 : 300);
const clip = (key, value) => String(value).trim().slice(0, maxLen(key));

function read() {
  try {
    const v = JSON.parse(window.sessionStorage.getItem(KEY) || "null");
    return v && typeof v === "object" ? v : null;
  } catch {
    return null;
  }
}

// document.referrer only when it comes from another site (internal navigation is not a source)
function externalReferrer() {
  try {
    const ref = document.referrer;
    if (!ref) return null;
    const u = new URL(ref);
    if (!/^https?:$/.test(u.protocol) || u.host === window.location.host) return null;
    return clip("referrer", ref);
  } catch {
    return null;
  }
}

/** Record this page load's attribution. Safe to call more than once. */
export function captureAttribution() {
  if (typeof window === "undefined") return;
  try {
    const saved = read();
    const next = { ...(saved || {}) };

    const params = new URLSearchParams(window.location.search);
    const incoming = {};
    for (const k of CAMPAIGN_KEYS) {
      const v = params.get(k);
      if (v && v.trim()) incoming[k] = clip(k, v);
    }
    if (Object.keys(incoming).length) {
      // A new campaign click replaces the previous one as a whole (no mixing of
      // an old utm_term with a new campaign). Landing page / referrer stay first-of-session.
      for (const k of CAMPAIGN_KEYS) delete next[k];
      Object.assign(next, incoming);
    }

    if (!saved) {
      next.landing_page = clip("landing_page", window.location.pathname + window.location.search);
      next.referrer = externalReferrer();
    }

    window.sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // storage blocked (private mode, etc.): forms just send no attribution
  }
}

/**
 * The visit's attribution, with every field present (null when unknown):
 * { utm_source, utm_medium, utm_campaign, utm_term, utm_content, gclid, gbraid, wbraid, landing_page, referrer }
 */
export function getAttribution() {
  const saved = (typeof window !== "undefined" && read()) || {};
  const out = {};
  for (const k of ATTRIBUTION_FIELDS) {
    const v = saved[k];
    out[k] = typeof v === "string" && v ? v.slice(0, maxLen(k)) : null;
  }
  return out;
}
